import { Select } from "antd";
import clsx from 'clsx';
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../../../../../../../contexts/AuthContext";
import query from "../../../../../../../server/constants.api";
import useFetch from "../../../../../../../server/useFetch";
import { buildOptionsForSelect } from "../../../../../../../utils/hooks/serialization";
import { buildPropertyForModal, findPropertiesByType, getListOfMandatoryAndOptionalProperties, isFamilyOrClassOrTenantProperty } from "../../ItemsConfig/ItemsConfig";
import ItemCreateEditModal from "../ItemCreateEditModal/ItemCreateEditModal";

const ItemCreate = ({ isModelOpen, record, onCloseModal, onCreationSuccess }) => {

    const { t } = useTranslation();
    const { tenant } = useAuthContext();
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);
    const [familiesFromTenant, setFamiliesFromTenant] = useState([]);

    const [itemToPost, setItemToPost] = useState(null);

    //------Create Item------
    const {
        data: addItemData,
        isFetching: addItemIsFetching,
        error: addItemError,
        isSuccess: addItemIsSuccess,
        isError: addItemIsError,
        refetch: addItemRefetch,
        remove: removeAddItem
    } = useFetch(
        '/items',
        'createItem',
        itemToPost,
        false,
        'POST',
        query.middlewareURL);


    useEffect(() => {
        if (itemToPost && !addItemIsFetching) {
            addItemRefetch();
        }
    }, [itemToPost]);


    const updateItemToPost = (newItemToPost) => {
        setItemToPost(newItemToPost);
    }


    //------Tenant, family and class dropdowns------
    const {
        data: subTenants,
        refetch: refetchSubTenants,
        isFetching: isFetchingSubTenants,
        remove: removeSubTenants
    } = useFetch('/items/distinct-properties',
        'getSubTenantsForCreateItem',
        {
            tenant: tenant,
            properties: "tenant",
        },
        false,
        null,
        query.middlewareURL
    )

    const {
        data: distinctProperties,
        refetch: refetchDistinctProperties,
        isFetching: isFetchingDistinctProperties,
        remove: removeDistinctProperties
    } = useFetch('/items/distinct-properties',
        'getDistinctPropertiesForCreateItem',
        {
            tenant: selectedTenant,
            family: selectedFamily,
            properties: selectedFamily ? 'family,class' : 'family',
        },
        false,
        null,
        query.middlewareURL
    )


    //------Specific Item------
    const {
        data: itemFullPropertiesByClass,
        isFetching: isFetchingItemFullPropertiesByClass,
        refetch: refetchItemFullPropertiesByClass,
        remove: removeItemFullPropertiesByClass
    } = useFetch('/items/properties',
        'getItemFullPropertiesByClass',
        {
            "full-properties": true,
            family: selectedFamily,
            class: selectedClass,
        },
        false,
        null,
        query.middlewareURL);

    /**
     * Used for when the modal is opened or closed
     */
    useEffect(() => {
        if (!isModelOpen) {
            removeSubTenants();
            removeDistinctProperties();
            removeItemFullPropertiesByClass();
            cleanStates([setSelectedTenant, setSelectedFamily, setSelectedClass, setFamiliesFromTenant]);
        } else {
            refetchSubTenants();
        }
    }, [isModelOpen]);

    useEffect(() => {
        if (selectedTenant) {
            refetchDistinctProperties();
        }
    }, [selectedTenant]);

    useEffect(() => {
        if (selectedFamily) {
            refetchDistinctProperties();
        }
    }, [selectedFamily]);

    useEffect(() => {
        if (selectedClass) {
            refetchItemFullPropertiesByClass();
        } else {
            removeItemFullPropertiesByClass();
        }
    }, [selectedClass]);

    /**
     * Used for storing the properties for the family before the class is selected
     */
    useEffect(() => {
        if (Array.isArray(distinctProperties)) {
            setFamiliesFromTenant(distinctProperties);
        }
    }, [distinctProperties]);

    /**
     * Serialize the record to be shown in the modal
     * @param {*} recordToSerialize 
     * @returns 
     */
    const serializeRecord = (recordToSerialize) => {
        if (!recordToSerialize || !isModelOpen) return null;

        const recordSerialized = {
            mandatoryProperties: [],
            optionalProperties: []
        }

        const recordCopy = {
            mandatoryProperties: [...recordToSerialize.mandatoryProperties],
            optionalProperties: [...recordToSerialize.optionalProperties],
        };

        // Add the specific properties when the class is selected
        if (itemFullPropertiesByClass && !isFetchingItemFullPropertiesByClass) {
            const specificProperties = findPropertiesByType("Specific", itemFullPropertiesByClass);
            const properties = getListOfMandatoryAndOptionalProperties([specificProperties]);
            recordCopy.mandatoryProperties = [...recordCopy.mandatoryProperties, ...properties.mandatoryProperties];
            recordCopy.optionalProperties = [...recordCopy.optionalProperties, ...properties.optionalProperties];
        }

        recordCopy.mandatoryProperties?.forEach(property => {
            const newProperty = transformProperty(property, true);
            recordSerialized.mandatoryProperties.push(newProperty);
        });

        recordCopy.optionalProperties?.forEach(property => {
            const newProperty = transformProperty(property);
            recordSerialized.optionalProperties.push(newProperty);
        });

        return recordSerialized;

    }

    /**
     * Build the property for the modal
     * @param {*} property Property to build
     * @param {*} isMandatory If the property is mandatory
     * @returns The property for the modal
     */
    const transformProperty = (property, isMandatory = false) => {
        const isFamilyOrClassOrTenant = isFamilyOrClassOrTenantProperty(property.property);

        if (isFamilyOrClassOrTenant) {
            return {
                ...property,
                fieldType: 'Custom',
                required: isMandatory,
                customObject: (form) => {
                    return (
                        <Select
                            listHeight={200}
                            placeholder={<span style={{ color: "lightgrey" }}>{t("Select Value")}</span>}
                            size={'large'}
                            bordered={false}
                            options={getOptions(property.property)}
                            className={clsx('select-atomic', 'full-width')}
                            onChange={(value) => {
                                updateSelectedProperty(property.property, value)
                                updateFormFields(form, property.property, value);
                            }}
                            disabled={isFetchingDistinctProperties || isFetchingSubTenants || isToDisabledProperty(property.property)}
                        />
                    )
                }
            }
        } else {
            return buildPropertyForModal(property, false, isMandatory);
        }
    }

    /**
     * Check if the property is to be disabled
     * @param {*} propertyName name of the property
     * @returns 
     */
    const isToDisabledProperty = (propertyName) => {
        if (propertyName === "family") {
            return !selectedTenant;
        } else if (propertyName === "class") {
            return !selectedFamily;
        } else {
            return false;
        }
    }

    /**
     * Clean the states 
     * @param {*} setFunctionsOfStatesToClean Array of states to clean
     */
    const cleanStates = (setFunctionsOfStatesToClean) => {
        setFunctionsOfStatesToClean.forEach(setFunction => {
            setFunction(null);
        });
    }

    /**
     * Update the selected property
     * @param {*} property The name of the property 
     * @param {*} value The value of the property selected
     */
    const updateSelectedProperty = (property, value) => {
        if (property === "tenant") {
            setSelectedTenant(value);
            cleanStates([setSelectedFamily, setSelectedClass]);
        } else if (property === "family") {
            setSelectedFamily(value);
            setSelectedClass(null);
        } else if (property === "class") {
            setSelectedClass(value);
        }
    }
    /**
     * Update the form fields for tenant, family and class
     * @param {*} form 
     * @param {*} property 
     * @param {*} value 
     */
    const updateFormFields = (form, property, value) => {
        if (property === "tenant") {
            form.setFieldsValue({
                family: null,
                class: null
            });
        } else if (property === "family") {
            form.setFieldsValue({
                class: null
            });
        }
    }

    /**
     * Get options for select field
     * @param {string} property The name of the property 
     * @returns {Array} The options for the select field
     */
    const getOptions = (property) => {
        if (property === "tenant") {
            const options = [tenant, ...(Array.isArray(subTenants) ? subTenants : [])];
            return buildOptionsForSelect(options);
        } else if (property === "family") {
            return buildOptionsForSelect(familiesFromTenant);
        } else if (property === "class") {
            if (!selectedFamily) return null;
            return buildOptionsForSelect(distinctProperties?.class);
        } else {
            return null;
        }
    }



    return (
        <ItemCreateEditModal
            isModelOpen={isModelOpen}
            record={serializeRecord(record)}
            onCloseModal={onCloseModal}
            onSuccess={onCreationSuccess}
            setItemData={updateItemToPost}
            isToEdit={false}
            removeFetch={removeAddItem}
            isFetching={addItemIsFetching}
            isSuccess={addItemIsSuccess}
            isError={addItemIsError}
            error={addItemError}
        />
    )
};

export default ItemCreate;